import { KeywordTableState } from '../../../components/muiLegacy/Tables/KeywordTable/logic/useKeywordTableState';
import { chain } from 'lodash';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { QueuedItemStatus } from '../../../graphql/projects/models/QueuedItemStatus';
import { ProjectGq } from '../../../graphql/projects/models/ProjectGq';
import { ProgressDateSelectBag } from './useProgressDateSelect';

export function useApplyProgressDateSelect(
  keywordTableState: KeywordTableState,
  dateSelectBag: ProgressDateSelectBag,
  project: ProjectGq
) {
  // Apply date selection
  useEffect(() => {
    const filteredTrackingRequests = chain(project.projectSerpTrackingRequests)
      .filter((t) => t.status === QueuedItemStatus.SUCCEEDED)
      .filter(
        (pstr) =>
          dateSelectBag.dateMin === null ||
          moment.utc(pstr.scheduledTime).isAfter(dateSelectBag.dateMin) ||
          moment.utc(pstr.scheduledTime).isSame(dateSelectBag.dateMin)
      )
      .filter(
        (pstr) =>
          dateSelectBag.dateMax === null ||
          moment.utc(pstr.scheduledTime).isBefore(dateSelectBag.dateMax) ||
          moment.utc(pstr.scheduledTime).isSame(dateSelectBag.dateMax)
      )
      .orderBy((f) => f.scheduledTime)
      .value();

    // Decrease frequency of data points
    // Rules:
    // - Always show the first data point
    // - Always show the first "rank tracking" data point
    // - Always show the last data points
    // - Adapt frequency for others ()

    const firstTrackingIndex =
      filteredTrackingRequests.findIndex((f) => f.includesActualTrackingData) ??
      filteredTrackingRequests.length - 1;

    const dataPointsAtEnd = 4;
    const endSectionIndex = Math.max(
      firstTrackingIndex + 1,
      filteredTrackingRequests.length - dataPointsAtEnd
    );

    const firstDataPoints = filteredTrackingRequests.slice(0, firstTrackingIndex + 1);
    let middleDataPoints = filteredTrackingRequests.slice(firstTrackingIndex + 1, endSectionIndex);
    const endDataPoints = filteredTrackingRequests.slice(endSectionIndex);

    if (filteredTrackingRequests.length < 8) {
      // Do nothing
    } else if (filteredTrackingRequests.length < 15) {
      // Adjust frequency to every second week
      middleDataPoints = middleDataPoints.filter((d, index) => !!(index % 2));
    } else if (filteredTrackingRequests.length < 30) {
      // Adjust frequency to monthly
      let months = [...firstDataPoints].map((dp) => moment.utc(dp.scheduledTime).format('YYYY MM'));

      middleDataPoints = middleDataPoints.filter((d, index) => {
        let month = moment.utc(d.scheduledTime).format('YYYY MM');
        if (months.includes(month)) {
          return false;
        }
        months.push(month);
        return true;
      });
    } else {
      // Adjust frequency to quarterly
      let quarters = [...firstDataPoints].map(
        (dp) => moment.utc(dp.scheduledTime).format('YYYY') + moment.utc(dp.scheduledTime).quarter()
      );

      middleDataPoints = middleDataPoints.filter((dp, index) => {
        let quarter =
          moment.utc(dp.scheduledTime).format('YYYY') + moment.utc(dp.scheduledTime).quarter();
        if (quarters.includes(quarter)) {
          return false;
        }
        quarters.push(quarter);
        return true;
      });
    }

    // Combine the slices
    const filteredTrackingIds = chain(
      [...firstDataPoints, ...middleDataPoints, ...endDataPoints].map((dp) => dp.id)
    )
      .orderBy((d) => d)
      .uniq()
      .value();

    // Apply filter
    keywordTableState.setFilterState((prev) => ({
      ...prev,
      progressRequestIds: filteredTrackingIds,
    }));
  }, [dateSelectBag.dateMin, dateSelectBag.dateMax, project.projectSerpTrackingRequests]);
}
